import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import IconButton from 'common/buttons/IconButton/IconButton';
import DialogV2 from 'common/modal/DialogV2';
import Notifier from 'common/helpers/Notifier';
import { BarLoader } from '@unite-us/app-components';
import {
  useDeletePersonalRelationship,
  useFetchPersonalRelationships,
} from '../Common/api';
import RelationshipCard from '../Common/RelationshipCard';
import RelationshipDeleteContent from '../Common/RelationshipDeleteContent';
import RelationshipListItem from '../Common/RelationshipListItem';
import RelationshipsList from '../Common/RelationshipsList';
import useFamilyMemberForm from './forms/useFamilyMemberForm';
import './FamilyMember.scss';

const DISPLAY_NAME = 'family-members';

export const FamilyMember = ({
 person, className, sectionTitle, shouldPortalizeDialogs, showHeader,
}) => {
  if (isEmpty(person)) {
    return null;
  }

  const {
    data: personalRelationships = [], isFetching, isError,
  } = useFetchPersonalRelationships({
    personId: person.id,
    familyMember: true,
    careTeamMember: false,
  });

  // Use prop from parent
  const finalShouldPortalize = shouldPortalizeDialogs ?? false;

  const dialogRef = useRef(null);
  React.useEffect(() => {
    if (isError) {
      Notifier.dispatch('Error fetching family members');
    }
  }, [isError]);
  const [currentPersonalRelationship, setCurrentPersonalRelationship] =
    useState(null);
  const [FamilyMemberFormDialog, openFamilyMemberForm] = useFamilyMemberForm({
    personId: person.id,
    shouldPortalizeDialogs: finalShouldPortalize,
  });

  const { deleteRecord: deletePersonalRelationship } =
    useDeletePersonalRelationship();
  const confirmDelete = async () => {
    dialogRef.current.closeDialog();
    const response = await deletePersonalRelationship(
      currentPersonalRelationship.id,
      {},
    );
    if (isHttpSuccess(response.status)) {
      Notifier.dispatch(response.status, 'Family member successfully deleted.');
    }
  };

  const deleteButtonOnClickHandler = (e, pr) => {
    e.stopPropagation();
    setCurrentPersonalRelationship(pr);
    dialogRef.current.openDialog();
  };
  const editButtonClickHandler = (e, pr) => {
    e.stopPropagation();
    openFamilyMemberForm(pr);
  };

  const content = (
    <>
      {isFetching ? (
        <BarLoader className="h-60" size="full" />
      ) : (
        <RelationshipsList name="family">
          {personalRelationships.map((pr) => (
            <RelationshipListItem
              key={pr.id}
              personalRelationshipId={pr.id}
              fullName={pr.related_person?.full_name}
              relationship={pr.relationship}
              onClick={() => openFamilyMemberForm(pr)}
              linkedFamilyMemberId={
                pr.shouldDisplayLink ? pr.related_person.id : ''
              }
            >
              <IconButton
                className={classNames(`${DISPLAY_NAME}__delete-btn`, 'pr-2')}
                labelText="Delete"
                icon="Delete"
                onClick={(e) => deleteButtonOnClickHandler(e, pr)}
                removeMargin
              />
              <IconButton
                className={`${DISPLAY_NAME}__edit-btn`}
                labelText="Edit"
                icon="Pencil"
                onClick={(e) => editButtonClickHandler(e, pr)}
                removeMargin
              />
            </RelationshipListItem>
          ))}
        </RelationshipsList>
      )}
      {FamilyMemberFormDialog}
      <DialogV2
        ref={dialogRef}
        title="Delete Family Member"
        confirmLabel="Delete"
        cancelHandler={dialogRef?.current?.closeDialog || (() => {})}
        confirmationHandler={confirmDelete}
      >
        <RelationshipDeleteContent memberType="Family" />
      </DialogV2>
    </>
  );

  if (!showHeader) {
    return (
      <div className={classNames(DISPLAY_NAME, className)}>
        <div className="mb-2 flex items-center justify-between gap-4">
          {sectionTitle ? (
            <h3 className="relationship-card-header__title text-base font-semibold leading-6 text-brand-blue">
              {sectionTitle}
            </h3>
          ) : <div />}
          <IconButton
            id="add-family-member-icon-button"
            labelText="Add Family Member"
            icon="Add"
            onClick={() => openFamilyMemberForm(null)}
          />
        </div>
        {content}
      </div>
    );
  }

  return (
    <RelationshipCard
      title="Family Members"
      addButtonLabel="Add Family Member"
      className={classNames(DISPLAY_NAME, className)}
      onAddClick={() => openFamilyMemberForm(null)}
    >
      {content}
    </RelationshipCard>
  );
};

FamilyMember.propTypes = {
  className: PropTypes.string,
  person: PropTypes.object.isRequired,
  sectionTitle: PropTypes.string,
  shouldPortalizeDialogs: PropTypes.bool,
  showHeader: PropTypes.bool,
};

FamilyMember.defaultProps = {
  className: '',
  sectionTitle: '',
  shouldPortalizeDialogs: false,
  showHeader: true,
};

export default connect()(FamilyMember);
